/**
 * ⚠ PENDING RETIREMENT (timeline-inline ruling 2026-09-19,
 * `docs/specs/strategic-architecture/timeline-inline-message-spec.md` §3.3). The entity-profile
 * timeline no longer opens this modal — its rows render the message body in place. Remaining
 * callers: a CrmSearch content hit, the record panel's D6 open-email flow, and any `?msg=N` link.
 * DELETE this component (and `MailParticipants` with it) once both of those callers render the body
 * through `TimelineEmailBody` / `InlineMessageBody` instead. Its tests stay green until then.
 *
 * Pillar F.3 — "Open" on a mail item opens a MODAL, not a new window/page.
 *
 * Owner ruling 2026-08-19: the body is fetched ON DEMAND per message (never preloaded for a list) and
 * stays entitlement-gated per read — both of which are properties of `CrmMessageDetail`, which this
 * modal simply hosts. Opening the modal mounts that detail for the first time, so the meta,
 * participant and body reads all happen on open and not before.
 *
 * The FULL PAGE is deliberately kept: `?sel=msg:N` still renders `CrmMessageView`, so every existing
 * deep link and shared URL keeps working, and "Open full view ↗" inside the modal is the path to it.
 * The modal itself is URL-backed too (the `msg` selection param, exactly the `note` overlay's model),
 * so a message opened over a profile is shareable and Back closes it back onto the profile — the
 * navigation spec's "a clickable item must resolve a deep link" rule, which a `useState` modal would
 * have quietly broken for a surface that previously deep-linked.
 *
 * Modal mechanics follow the siblings (`CrmRenameModal` / `CrmViewSaveModal`): the shared
 * `modalBackdrop` / `modalCard` classes, backdrop-mousedown and Escape close, and focus moved into the
 * card on open with a document-level key handler so Escape works wherever focus lands.
 */
import { useEffect, useRef } from 'react';

import { CrmMessageDetail } from './CrmMessageDetail';
import type { OpenPerson } from './CrmParticipants';
import styles from './crm.module.css';

interface Props {
  emailId: number;
  onClose: () => void;
  /** Navigate to the full, deep-linkable message page (`?sel=msg:N`). */
  onOpenFullView: (emailId: number) => void;
  /** In-place profile navigation for a linked participant. */
  openPerson?: OpenPerson;
}

export function CrmMessageModal({ emailId, onClose, onOpenFullView, openPerson }: Props) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Move focus into the card so Tab walks the modal and the close button is one key away. Not a
  // focus TRAP: the siblings do not trap either, and adding one only here would behave differently
  // from every other overlay in the tab.
  useEffect(() => { cardRef.current?.focus(); }, [emailId]);

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div
        ref={cardRef}
        tabIndex={-1}
        className={styles.msgModalCard}
        role="dialog"
        aria-modal="true"
        aria-label="Message"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.msgModalHead}>
          <button
            type="button"
            className={styles.gridMiniBtn}
            onClick={() => onOpenFullView(emailId)}
          >
            Open full view ↗
          </button>
          <button
            type="button"
            className={styles.noteCloseBtn}
            aria-label="Close message"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className={styles.msgModalBody}>
          <CrmMessageDetail emailId={emailId} openPerson={openPerson} />
        </div>
      </div>
    </div>
  );
}
